import React from 'react';
import { motion } from 'framer-motion';

export default function SecuritySection() {
  return (
    <section className="w-full max-w-[1240px] mx-auto px-4 md:px-8 pt-[112px] pb-[112px] grid grid-cols-1 lg:grid-cols-[1fr_1fr] gap-12 lg:gap-20 items-center">
      <link href="https://fonts.googleapis.com/css2?family=Anton&family=Inter:wght@400;500;700&display=swap" rel="stylesheet" />
      
      <div className="flex flex-col">
        <motion.h2 
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-50px" }}
          transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          className="text-[56px] md:text-[72px] font-['Anton',sans-serif] font-normal text-[#000] uppercase leading-[1.1] md:leading-[80px] mb-6"
        >
          SAFE AT EVERY STEP
        </motion.h2>
        
        <motion.p 
          initial={{ opacity: 0, x: -40 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, margin: "-50px" }}
          transition={{ duration: 0.6, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
          className="text-[18px] text-gray-700 leading-[1.5] mb-10"
        >
          14.8 million customers, businesses and banks use our platform every day. They move $16 billion every month. Here's how we keep each dollar safe.
        </motion.p>
        
        <div className="flex flex-col border-b border-gray-200">
          <motion.div 
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.6, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="flex items-start gap-6 py-6 border-t border-gray-200"
          >
            <div className="w-12 h-12 rounded-full border border-gray-200 flex items-center justify-center flex-shrink-0 mt-1">
              <img src="https://cdn.jiro.build/Random/Federally%20regulated.svg" alt="Federally regulated" className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-black mb-2">Federally regulated</h3>
              <p className="text-gray-600 leading-relaxed max-w-[420px]">
                Regulated nationwide, with 65 licences worldwide to keep your money moving.
              </p>
            </div>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.6, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="flex items-start gap-6 py-6 border-t border-gray-200"
          >
            <div className="w-12 h-12 rounded-full border border-gray-200 flex items-center justify-center flex-shrink-0 mt-1">
              <img src="https://cdn.jiro.build/Random/Safeguarded%20funds.svg" alt="Safeguarded funds" className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-black mb-2">Safeguarded funds</h3>
              <p className="text-gray-600 leading-relaxed max-w-[420px]">
                Your money is protected in a safeguarded, separate account.
              </p>
            </div>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.6, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="flex items-start gap-6 py-6 border-t border-gray-200"
          >
            <div className="w-12 h-12 rounded-full border border-gray-200 flex items-center justify-center flex-shrink-0 mt-1">
              <img src="https://cdn.jiro.build/Random/Specialized%20anti-fraud%20tech.svg" alt="Specialized anti-fraud tech" className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-black mb-2">Specialized anti-fraud tech</h3>
              <p className="text-gray-600 leading-relaxed max-w-[420px]">
                Over 1,000 experts and our smart systems run 7 million checks every day to spot and stop fraud.
              </p>
            </div>
          </motion.div>
        </div>

        <motion.button 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-50px" }}
          transition={{ duration: 0.6, delay: 0.6, ease: [0.16, 1, 0.3, 1] }}
          className="mt-10 px-[40px] py-[14px] bg-[#46E8D4] text-[#111] font-sans font-medium rounded-full w-fit hover:bg-[#073634] hover:text-[#46E8D4] transition-colors relative z-10 text-[22px] leading-[32px] inline-flex items-center justify-center gap-[10px]"
        >
          See our security
        </motion.button>
      </div>

      <motion.div 
        initial={{ opacity: 0, x: 80 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true, margin: "-50px" }}
        transition={{ duration: 0.7, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
        className="w-full relative flex items-center justify-end"
      >
        <div className="relative rounded-[32px] overflow-hidden bg-gray-200 flex items-center justify-center w-full max-w-[555px] aspect-[61/81] mx-auto lg:mx-0">
          <img 
            src="https://cdn.jiro.build/Random/man%20on%20a%20phone.png" 
            alt="Secure banking user" 
            className="w-full h-full object-cover"
          />
          
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2, duration: 0.5 }}
            className="absolute top-[65%] md:top-[60%] sm:left-[10%] left-0 h-[80px] pt-[10px] pb-[10px] pr-[25px] pl-[10px] mt-0 mb-0 ml-[30px] flex items-center gap-[13px] flex-shrink-0 rounded-[99px] bg-white z-20 shadow-[0_10px_40px_rgba(0,0,0,0.15)]"
          >
            <img src="https://cdn.jiro.build/Random/P%20logo.svg" alt="P logo" className="w-[61px] h-[61px]" />
            <span className="text-[#000] font-sans text-[24px] font-medium leading-[1.2] tracking-[-0.48px] whitespace-nowrap">
              24/7 customer support
            </span>
          </motion.div>
        </div>
      </motion.div>
    </section>
  );
}